import React, { useEffect, useMemo, useState } from "react";
import Modal from "@/components/ui/Modal";
import {
  Button,
  Dropdown,
  type DropdownItem,
  Input,
  Textarea,
  useToast,
} from "@/components/ui";
import { useFormik } from "formik";
import * as Yup from "yup";
import { ChevronDown } from "lucide-react";
import {
  providerLabel,
  useVoiceOptions,
} from "../components/useVoiceOptions";
import {
  callingAgentsService,
  type CallingAgent,
  type CallingAgentPrerequisites,
  type UpdateCallingAgentRequest,
} from "@/services/api/callingAgents";

interface Props {
  isOpen: boolean;
  onClose: () => void;
  agent: CallingAgent | null;
  onSuccess: () => void;
}

interface AgentFormValues {
  type: string;
  display_name: string;
  description: string;
  voice_provider: string;
  voice_id: string;
  language: string;
  first_message: string;
  prompt: string;
}

const AGENT_TYPES = ["outbound", "inbound"];
const FALLBACK_PREREQUISITES: CallingAgentPrerequisites = {
  voice_providers: ["vapi", "retell", "twilio"],
  languages: ["en", "hi", "multi"],
};

const LANGUAGE_LABELS: Record<string, string> = {
  en: "English",
  hi: "Hindi",
  multi: "Multilingual",
};

const labelize = (value: string) =>
  LANGUAGE_LABELS[value] ??
  value
    .split(/[-_]/)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1))
    .join(" ");

const EditAgentModal: React.FC<Props> = ({
  isOpen,
  onClose,
  agent,
  onSuccess,
}) => {
  const { success, error: toastError } = useToast();
  const [prerequisites, setPrerequisites] =
    useState<CallingAgentPrerequisites>(FALLBACK_PREREQUISITES);

  useEffect(() => {
    if (!isOpen) return;

    callingAgentsService
      .prerequisites()
      .then((res) => {
        const attributes = res?.attributes;
        if (
          attributes?.voice_providers?.length ||
          attributes?.languages?.length
        ) {
          setPrerequisites({
            voice_providers:
              attributes.voice_providers ?? FALLBACK_PREREQUISITES.voice_providers,
            languages: attributes.languages ?? FALLBACK_PREREQUISITES.languages,
          });
        }
      })
      .catch(() => {});
  }, [isOpen]);

  const voiceProviders = useMemo(
    () =>
      prerequisites.voice_providers.length
        ? prerequisites.voice_providers
        : FALLBACK_PREREQUISITES.voice_providers,
    [prerequisites.voice_providers],
  );

  const languages = useMemo(
    () =>
      prerequisites.languages.length
        ? prerequisites.languages
        : FALLBACK_PREREQUISITES.languages,
    [prerequisites.languages],
  );

  const formik = useFormik<AgentFormValues>({
    initialValues: {
      type: agent?.type ?? "outbound",
      display_name: agent?.display_name ?? "",
      description: agent?.description ?? "",
      voice_provider: agent?.voice_provider ?? "",
      voice_id: agent?.voice_id ?? "",
      language: agent?.language ?? "",
      first_message: agent?.settings?.first_message ?? "",
      prompt: agent?.prompt ?? "",
    },
    validationSchema: Yup.object({
      type: Yup.string().required("Required"),
      display_name: Yup.string().required("Required"),
      voice_provider: Yup.string().required("Required"),
      voice_id: Yup.string().required("Required"),
      language: Yup.string().required("Required"),
      first_message: Yup.string().required("Required"),
      prompt: Yup.string().required("Required"),
    }),
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      if (!agent) return;
      try {
        const payload: UpdateCallingAgentRequest = {
          type: values.type,
          display_name: values.display_name,
          description: values.description,
          voice_provider: values.voice_provider,
          voice_id: values.voice_id,
          language: values.language,
          prompt: values.prompt,
          settings: {
            ...(agent.settings ?? {}),
            first_message: values.first_message,
          },
          data_details: agent.data_details ?? {},
        };

        await callingAgentsService.update(agent.uuid, payload);
        success("Agent updated successfully");
        onSuccess();
        onClose();
      } catch (e: any) {
        toastError(e?.message || "Failed to update agent");
      } finally {
        setSubmitting(false);
      }
    },
  });

  const voiceOptions = useVoiceOptions({
    provider: formik.values.voice_provider,
    language: formik.values.language,
    voiceId: formik.values.voice_id,
    onSelect: (id) => formik.setFieldValue("voice_id", id),
  });

  // Changing provider/language invalidates the chosen voice
  const setAndClearVoice = (field: string) => (v: string) => {
    formik.setFieldValue(field, v);
    formik.setFieldValue("voice_id", "");
  };

  const dropdownTrigger = (label: string, placeholder = "Select") => (
    <div className="h-10 bg-white border border-gray-300 rounded-lg px-3 py-2 text-gray-700 flex justify-between items-center cursor-pointer w-full text-sm">
      <span className={label ? "text-gray-900" : "text-gray-400"}>
        {label || placeholder}
      </span>
      <ChevronDown size={16} className="text-gray-400" />
    </div>
  );

  const makeItems = (
    values: string[],
    setter: (v: string) => void,
    formatter: (v: string) => string = labelize,
  ): DropdownItem[] =>
    values.map((v) => ({
      label: formatter(v),
      onClick: () => setter(v),
    }));

  const f = (name: keyof AgentFormValues) => ({
    name,
    value: formik.values[name],
    onChange: formik.handleChange,
    onBlur: formik.handleBlur,
  });

  const err = (name: keyof AgentFormValues) =>
    formik.touched[name] && formik.errors[name]
      ? String(formik.errors[name])
      : null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Calling Agent"
      size="xl"
      footer={
        <div className="flex justify-end gap-3 px-2">
          <Button
            type="submit"
            form="edit-agent-modal-form"
            disabled={formik.isSubmitting}
            className="bg-primary-button hover:bg-primary-button text-white"
          >
            {formik.isSubmitting ? "Saving..." : "Save Changes"}
          </Button>
          <Button variant="outline" type="button" onClick={onClose}>
            Cancel
          </Button>
        </div>
      }
    >
      <form
        id="edit-agent-modal-form"
        onSubmit={formik.handleSubmit}
        className="px-4 py-4 space-y-4"
      >
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs font-medium">Display Name *</label>
            <Input {...f("display_name")} />
            {err("display_name") && (
              <p className="text-red-500 text-sm mt-1">{err("display_name")}</p>
            )}
          </div>

          <div>
            <label className="text-xs font-medium">Agent Type *</label>
            <Dropdown
              trigger={dropdownTrigger(labelize(formik.values.type))}
              items={makeItems(AGENT_TYPES, (v) =>
                formik.setFieldValue("type", v),
              )}
              align="left"
              triggerClassName="py-0"
            />
            {err("type") && (
              <p className="text-red-500 text-sm mt-1">{err("type")}</p>
            )}
          </div>

          <div>
            <label className="text-xs font-medium">Voice Provider *</label>
            <Dropdown
              trigger={dropdownTrigger(
                formik.values.voice_provider
                  ? providerLabel(formik.values.voice_provider)
                  : "",
              )}
              items={makeItems(
                voiceProviders,
                setAndClearVoice("voice_provider"),
                providerLabel,
              )}
              align="left"
              triggerClassName="py-0"
            />
            {err("voice_provider") && (
              <p className="text-red-500 text-sm mt-1">{err("voice_provider")}</p>
            )}
          </div>

          <div>
            <label className="text-xs font-medium">Language *</label>
            <Dropdown
              trigger={dropdownTrigger(labelize(formik.values.language))}
              items={makeItems(languages, setAndClearVoice("language"))}
              align="left"
              triggerClassName="py-0"
            />
            {err("language") && (
              <p className="text-red-500 text-sm mt-1">{err("language")}</p>
            )}
          </div>

          {/* Voice dropdown — full width so its menu (tags + play) fits the modal */}
          <div className="md:col-span-2">
            <label className="text-xs font-medium">Voice *</label>
            <Dropdown
              trigger={dropdownTrigger(
                voiceOptions.selectedLabel,
                voiceOptions.placeholder,
              )}
              items={voiceOptions.items}
              header={voiceOptions.header}
              align="left"
              triggerClassName="py-0"
            />
            {err("voice_id") && (
              <p className="text-red-500 text-sm mt-1">{err("voice_id")}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="text-xs font-medium">First Message *</label>
            <Input {...f("first_message")} />
            {err("first_message") && (
              <p className="text-red-500 text-sm mt-1">{err("first_message")}</p>
            )}
          </div>

          <div className="md:col-span-2">
            <label className="text-xs font-medium">Description</label>
            <Input {...f("description")} />
          </div>

          <div className="md:col-span-2">
            <label className="text-xs font-medium">Prompt *</label>
            <Textarea rows={10} className="text-sm" {...f("prompt")} />
            {err("prompt") && (
              <p className="text-red-500 text-sm mt-1">{err("prompt")}</p>
            )}
          </div>
        </div>
      </form>
    </Modal>
  );
};

export default EditAgentModal;
